/**
 * Pantalla de cambio de contraseña obligatorio.
 * Se muestra cuando un administrador resetea la contraseña de un usuario con
 * "Forzar cambio": el usuario no puede usar el sistema hasta establecer una nueva.
 */
import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FiLock, FiEye, FiEyeOff, FiLogOut } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { changeMyPassword } from '../../services/userService';

const ForcePasswordChange = () => {
  const { user, logout, clearMustChangePassword } = useAuth();

  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [show, setShow] = useState({ current: false, next: false, confirm: false });

  // Si ya no debe cambiar la contraseña, salir de esta pantalla
  if (user && !user.must_change_password) {
    return <Navigate to="/" replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const toggle = (field) => setShow(prev => ({ ...prev, [field]: !prev[field] }));

  const validate = () => {
    const next = {};
    if (!form.currentPassword) {
      next.currentPassword = 'Ingresa tu contraseña actual';
    }
    if (!form.newPassword) {
      next.newPassword = 'La nueva contraseña es requerida';
    } else if (form.newPassword.length < 8) {
      next.newPassword = 'Debe tener al menos 8 caracteres';
    } else if (form.newPassword === form.currentPassword) {
      next.newPassword = 'La nueva contraseña debe ser distinta a la actual';
    }
    if (form.newPassword !== form.confirmPassword) {
      next.confirmPassword = 'Las contraseñas no coinciden';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setErrors({});
    try {
      const result = await changeMyPassword(form.currentPassword, form.newPassword);
      if (result.success) {
        // Limpiar la obligación: el guard de esta pantalla redirige a "/"
        clearMustChangePassword();
      } else {
        setErrors({ general: result.message || 'No se pudo cambiar la contraseña' });
      }
    } catch (err) {
      setErrors({ general: 'Error cambiando la contraseña' });
    } finally {
      setSubmitting(false);
    }
  };

  const field = (name, label, showKey, placeholder) => (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type={show[showKey] ? 'text' : 'password'}
          name={name}
          value={form[name]}
          onChange={handleChange}
          placeholder={placeholder}
          autoComplete={name === 'currentPassword' ? 'current-password' : 'new-password'}
          style={{
            width: '100%',
            padding: '10px 40px 10px 12px',
            borderRadius: '8px',
            border: `1px solid ${errors[name] ? '#dc3545' : '#d1d5db'}`,
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />
        <button
          type="button"
          onClick={() => toggle(showKey)}
          aria-label={show[showKey] ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          style={{
            position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex'
          }}
        >
          {show[showKey] ? <FiEyeOff size={18} /> : <FiEye size={18} />}
        </button>
      </div>
      {errors[name] && (
        <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{errors[name]}</div>
      )}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #7B2D8E 0%, #834594 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        width: '100%',
        maxWidth: '440px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%', background: '#f3e8f7',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#7B2D8E', marginBottom: '12px'
          }}>
            <FiLock size={28} />
          </div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, margin: '0 0 6px', color: '#111827' }}>
            Cambia tu contraseña
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
            Por seguridad debes establecer una nueva contraseña antes de continuar
            {user?.email ? <> (<strong>{user.email}</strong>)</> : null}.
          </p>
        </div>

        {errors.general && (
          <div style={{
            background: '#fee2e2', color: '#991b1b', padding: '10px 12px',
            borderRadius: '8px', fontSize: '14px', marginBottom: '16px'
          }}>
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {field('currentPassword', 'Contraseña actual', 'current', 'La contraseña temporal asignada')}
          {field('newPassword', 'Nueva contraseña', 'next', 'Mínimo 8 caracteres')}
          {field('confirmPassword', 'Confirmar nueva contraseña', 'confirm', 'Repite la nueva contraseña')}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', padding: '12px', borderRadius: '8px', border: 'none',
              background: submitting ? '#a78bb0' : '#7B2D8E', color: 'white', fontSize: '15px',
              fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', marginTop: '8px'
            }}
          >
            {submitting ? 'Guardando...' : 'Cambiar contraseña'}
          </button>
        </form>

        <button
          type="button"
          onClick={logout}
          style={{
            width: '100%', marginTop: '14px', padding: '8px', borderRadius: '8px',
            background: 'none', border: 'none', color: '#6b7280', fontSize: '13px',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
          }}
        >
          <FiLogOut size={15} /> Cerrar sesión
        </button>
      </div>
    </div>
  );
};

export default ForcePasswordChange;
